import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import TestimonialsPage from './pages/TestimonialsPage';
import TestSeriesPage from './pages/TestSeriesPage';
import FreeQuizPage from './pages/FreeQuizPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/legal/TermsAndConditionsPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import DashboardPage from './pages/DashboardPage';
import AdminLayout from './components/AdminLayout';
import AdminFreeQuizPage from './pages/admin/FreeQuizPage';
import AdminBlogPage from './pages/admin/BlogPage';
import QuestionsPage from './pages/admin/QuestionsPage';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import TestDetailPage from './pages/TestDetailPage';
import Breadcrumb from './components/Breadcrumb';
import { ArrowUp } from 'lucide-react';
import QuizPage from './pages/QuizPage';
import { MetaTagsManager } from './components/MetaTagsManager';

interface AppProps {
  initialData?: any;
}

// Create a ScrollToTop component that will handle the scrolling
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!import.meta.env.SSR && typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
};

function App({ initialData }: AppProps) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!import.meta.env.SSR && typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark');
    }
  };

  const scrollToTop = () => {
    if (!import.meta.env.SSR && typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!import.meta.env.SSR && typeof window !== 'undefined') {
      const handleScroll = () => {
        setShowScrollTop(window.scrollY > 300);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <AuthProvider>
      <ScrollToTop />
      <MetaTagsManager />
      <div className={`min-h-screen ${isDarkMode ? 'dark' : ''}`}>
        <Routes>
          {/* Admin Routes */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/free-quiz" element={<AdminFreeQuizPage />} />
                    <Route path="/blog" element={<AdminBlogPage />} />
                    <Route path="/questions/:categoryId" element={<QuestionsPage />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          {/* Public Routes */}
          <Route
            path="/*"
            element={
              <div className="bg-white dark:bg-gray-900 transition-colors duration-200">
                <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
                <main className="pt-16">
                  <Breadcrumb />
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/test-series" element={<TestSeriesPage />} />
                    <Route path="/free-agriculture-quiz" element={<FreeQuizPage />} />
                    <Route path="/free-agriculture-quiz/:subjectSlug/:questionSlug" element={<QuizPage />} />
                    <Route path="/free-agriculture-quiz/:subjectId" element={<QuizPage />} />
                    <Route path="/testimonials" element={<TestimonialsPage />} />
                    <Route path="/blog" element={<BlogPage initialData={initialData} />} />
                    <Route path="/blog/:slug" element={<BlogPostPage initialData={initialData} />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsAndConditionsPage />} />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/test-series/:examId" element={<TestDetailPage />} />
                  </Routes>
                </main>
                <Footer />

                {/* Scroll to Top Button */}
                {showScrollTop && (
                  <button
                    onClick={scrollToTop}
                    className="fixed bottom-8 right-8 p-3 bg-green-800 hover:bg-green-900 text-white rounded-full shadow-lg transition-all duration-300 z-50"
                    aria-label="Scroll to top"
                  >
                    <ArrowUp className="h-6 w-6" />
                  </button>
                )}
              </div>
            }
          />
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;